import { useState } from "react";
import * as XLSX from "xlsx";
import axios from "axios";
import { X, Upload } from "lucide-react";
import { ImSpinner2 } from "react-icons/im";
import { showError, showSuccess } from "../../../utils/toastUtils";
import { useEffect } from "react";

const EXCEL_HEADER_MAP = {
  "slno": null, // ignore
  "service": "service",
  "itemdescription": "productName",
  "brand": "brand",
  "productusagetype": "productUsageType",
  "mrp": "mrp",
  "quantity": "quantity",
  "discount": "discountPercent",

  // calculated / ignored columns
  "price(mrpxqnty)": null,
  "finalprice": null,
  "unitprice": null,
};


const REQUIRED_FIELDS = [
  "productName",
  "brand", "service",
  "productUsageType",
  "mrp",
];
const ALLOWED_USAGE_TYPES = ["use_and_throw", "reusable"];

const normalizeRowKeys = (row) => {
  const normalized = {};

  Object.keys(row).forEach((key) => {
    const cleanKey = key
      .replace(/\s+/g, "")
      .replace(/\n/g, "")
      .replace(/[^a-zA-Z0-9]/g, "")
      .toLowerCase();

    const mappedKey = EXCEL_HEADER_MAP[cleanKey];

    if (mappedKey) {
      normalized[mappedKey] = row[key];
    }
  });

  return normalized;
};


const isValidDiscount = (value) => {
  if (value === undefined || value === "") return true;
  const num = Number(value);
  return !isNaN(num) && num >= 0 && num <= 100;
};

const UploadProductsExcelModal = ({ onClose, onSuccess }) => {
  const [rows, setRows] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState("");

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/vendors`
        );
        setVendors(res.data.data || []);
      } catch (err) {
        showError("Failed to load vendors");
      }
    };

    fetchVendors();
  }, []);


  const handleFileUpload = (file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const workbook = XLSX.read(e.target.result, { type: "binary" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(sheet);
      // console.log(Object.keys(data[0]));

      const parsed = data.map((row, index) => {
        row = normalizeRowKeys(row);
        const rawUsageType = row.productUsageType
          ?.toString()
          .trim()
          .toLowerCase();

        const isUsageTypeValid = ALLOWED_USAGE_TYPES.includes(rawUsageType);
        const isDiscountValid = isValidDiscount(row.discountPercent);
        const rawDiscount = row.discountPercent;

        let normalizedDiscount = 0;

        if (rawDiscount !== undefined && rawDiscount !== "") {
          const num = Number(rawDiscount);

          normalizedDiscount = num <= 1 ? num * 100 : num;
        }


        const missing = REQUIRED_FIELDS.filter(
          (f) => row[f] === undefined || row[f] === ""
        );

        return {
          productName: row.productName?.toString().trim(),
          brand: row.brand?.toString().trim(),
          service: row.service?.toString().trim(),
          productUsageType: rawUsageType,
          mrp: Number(row.mrp),
          quantity: row.quantity ? Number(row.quantity) : 1,
          discountPercent: Number(normalizedDiscount.toFixed(2)),

          _row: index + 2,
          _valid:
            missing.length === 0 &&
            isUsageTypeValid &&
            isDiscountValid,

          _missing: [
            ...missing,
            !isUsageTypeValid ? "Invalid productUsageType" : null,
            !isDiscountValid ? "Invalid discountPercent (0–100)" : null,
          ].filter(Boolean),
        };
      });

      setRows(parsed);
    };

    reader.readAsBinaryString(file);
  };

  const submit = async () => {
    if (!selectedVendor) {
      showError("Please select a vendor before uploading");
      return;
    }

    const validRows = rows.filter((r) => r._valid);

    if (!validRows.length) {
      showError("No valid rows to upload");
      return;
    }

    try {
      setIsSubmitting(true);

      await axios.post(
        `${process.env.REACT_APP_API_URL}/products/bulk-create`,
        {
          products: validRows.map(
            ({ _row, _valid, _missing, ...clean }) => ({
              ...clean,
              vendor: selectedVendor,
            })
          ),
        }
      );

      showSuccess(`${validRows.length} products uploaded successfully`);
      onSuccess();
      onClose();
    } catch (err) {
      showError(err?.response?.data?.message || "Bulk upload failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-[60%] max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-primaryColor via-primaryColor/70 to-primaryColor text-white px-6 py-5 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold">Upload Products via Excel</h2>
            <X
              onClick={onClose}
              className="size-6 cursor-pointer hover:scale-[1.05]"
            />
          </div>
          <p className="text-sm mt-1">
            Upload multiple products at once using an Excel file
          </p>
        </div>

        <div className="rounded-xl border border-yellow-300 bg-yellow-50 p-4 text-sm text-yellow-900">
          <p className="font-semibold mb-1">📌 Excel Naming Rules</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              Column names must be exactly:
              <code className="ml-1 bg-yellow-100 px-1 rounded">
                productName, brand, service, productUsageType, mrp, quantity, discountPercent
              </code>
            </li>
            <li>
              <b>discountPercent</b> must be a number between
              <code className="ml-1 bg-yellow-100 px-1 rounded">0–100</code>
            </li>
            <li>
              Allowed <b>productUsageType</b> values:
              <code className="ml-1 bg-yellow-100 px-1 rounded">
                reusable
              </code>
              ,
              <code className="ml-1 bg-yellow-100 px-1 rounded">
                use_and_throw
              </code>
            </li>
            <li>Don't add extra spaces & line breaks</li>
          </ul>
        </div>

        {/* Vendor Selector */}
        <div className="bg-gray-50 border rounded-xl p-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Select Vendor for All Products <span className="text-red-500">*</span>
          </label>

          <select
            value={selectedVendor}
            onChange={(e) => setSelectedVendor(e.target.value)}
            className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-1 focus:ring-primaryColor"
          >
            <option value="">-- Select Vendor --</option>

            {vendors.map((v) => (
              <option key={v._id} value={v._id}>
                {v.vendorName} ({v.ownerName})
              </option>
            ))}
          </select>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Upload Box */}
          <label className="border-2 border-dashed rounded-xl p-6 flex flex-col items-center cursor-pointer hover:bg-gray-50 transition">
            <Upload size={36} className="text-gray-400" />
            <p className="mt-2 text-sm font-medium">
              Click to upload Excel (.xlsx)
            </p>
            <input
              type="file"
              accept=".xlsx,.xls"
              hidden
              onChange={(e) => handleFileUpload(e.target.files[0])}
            />
          </label>

          {/* Preview */}
          {rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border rounded-lg">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-2 text-center">Row</th>
                    <th className="p-2">Product</th>
                    <th className="p-2">Brand</th>
                    <th className="p-2">Service</th>
                    <th className="p-2">Vendor</th>
                    <th className="p-2">Usage</th>
                    <th className="p-2 text-right">MRP</th>
                    <th className="p-2 text-center">Qty</th>
                    <th className="p-2 text-right">Discount %</th>
                    <th className="p-2 text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r._row}
                      className={!r._valid ? "bg-red-50" : ""}
                    >
                      <td className="p-2 text-center">{r._row}</td>
                      <td className="p-2">{r.productName}</td>
                      <td className="p-2">{r.brand}</td>
                      <td className="p-2">{r.service}</td>
                      <td className="p-2 text-xs text-gray-700">
                        {vendors.find(v => v._id === selectedVendor)?.vendorName || "-"}
                      </td>
                      <td className="p-2">{r.productUsageType}</td>
                      <td className="p-2 text-right">{r.mrp}</td>
                      <td className="p-2 text-center">{r.quantity}</td>
                      <td className="p-2 text-right">
                        {r.discountPercent}%
                      </td>
                      <td className="p-2 text-center">
                        {r._valid ? (
                          <span className="text-green-600 font-semibold">
                            Valid
                          </span>
                        ) : (
                          <span className="text-red-600 text-xs">
                            Missing: {r._missing.join(", ")}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 pb-6">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-6 py-3 rounded-xl border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition disabled:opacity-60"
          >
            Cancel
          </button>

          <button
            onClick={submit}
            disabled={isSubmitting}
            className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-[#f0653a] to-[#d94f2a] 
              text-white font-semibold active:scale-[0.98] transition-all
              disabled:opacity-80 flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <ImSpinner2 className="w-5 h-5 animate-spin" />
                Uploading...
              </>
            ) : (
              "Upload Products"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadProductsExcelModal;
